import React, { useEffect, useState } from 'react';
import "../GuidedExperience/GuidedExperienceWizard.css";
import { useTranslation } from 'react-i18next';
import DataTable from '../common/DataTable/DataTable';
import Checkbox from '../common/Checkbox/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import Dropdown, { Option } from '../common/Dropdown/Dropdown';
import Input from '../common/Input/Input';
import CurrencyInput from '../common/CurrencyInput/CurrencyInput';

interface ConfirmUnitsWizardProps {
    units: Record<string, unknown>[];
    setUnits: (value: Record<string, unknown>[]) => void;
    confirmedArr: number[];
    setConfirmedArr?: (value: number[]) => void;
    isBasePropType: boolean;
    multiresUnitSubtype: Record<string, unknown>[];
    multiresRowHouseUnitSubtype: Record<string, unknown>[];
    styleOptions: Option[];
    unitTypeSubtypeMap: Record<string, Option[]>;
    bathsOptions: ({
        value: string;
        text: string;
    } | {
        value: number;
        text: string;
    } | Option)[];
    purpose: "confirm" | "edit" | "rent" | "summary" | "affordable-summary";
}

export default function ConfirmUnitsWizard(props: ConfirmUnitsWizardProps): JSX.Element {
    const { t } = useTranslation(["guided_wizard, suite_breakdown_table"]);
    const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);

    const checkboxEventHandler = (e: React.ChangeEvent<Element>, index?: number) => {
        let newConfirmedArr = [...props.confirmedArr];
        if (index !== undefined) {
            const indexOf = newConfirmedArr.indexOf(index);
            if ((e.target as HTMLInputElement).checked) {
                if (indexOf < 0) {
                    newConfirmedArr.push(index);
                }
            } else {
                if (indexOf >= 0) {
                    newConfirmedArr.splice(indexOf, 1);
                }
            }
        } else {
            if (newConfirmedArr.length === props.units.length) {
                newConfirmedArr = [];
            } else {
                newConfirmedArr = [...Array(props.units.length).keys()];
            }
        }
        props.setConfirmedArr && props.setConfirmedArr(newConfirmedArr.sort());
    }

    const updateValue = (index: number, key: string, isNumber?: boolean, passedValue?: number | null | undefined, e?: React.ChangeEvent) => {
        const dataSet = [...props.units]
        if (e) {
            const target = e.target as HTMLSelectElement | HTMLInputElement
            if (target.type === "checkbox") {
                dataSet[index][key] = (target as HTMLInputElement).checked
            } else {
                const value = target.value.trim()
                if (isNumber && value !== "") {
                    let numericValue = Number(value)
                    if (isNaN(numericValue)) {
                        dataSet[index][key] = null
                        target.value = ""
                    } else if (numericValue < 0) {
                        numericValue = -1 * numericValue
                        dataSet[index][key] = numericValue
                        target.value = String(numericValue)
                    } else if (["unitCount", "renovatedUnitCount", "vacantUnitCount", "netLeasableArea"].includes(key)) {
                        numericValue = Math.trunc(numericValue)
                        dataSet[index][key] = numericValue
                        target.value = String(numericValue)
                    } else {
                        dataSet[index][key] = numericValue
                    }
                } else {
                    dataSet[index][key] = value === "" ? null : value
                    if (key === "styleTypeCode" && (!(value in props.unitTypeSubtypeMap) || !(props.unitTypeSubtypeMap[value].some((option) => option["value"] === (props.units[index]["unitSubtypeCode"] as string))))) {
                        dataSet[index]["unitSubtypeCode"] = null;
                    }
                }
            }
        } else {
            const numericValue = Number(passedValue)
            if (isNaN(numericValue)) {
                dataSet[index][key] = null
            } else {
                dataSet[index][key] = numericValue
            }
        }
        props.setUnits(dataSet);
    }

    const handleCurrencyChange = (value: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
        const index = name.split("-")[1];
        if (!isNaN(parseInt(index))) {
            updateValue(parseInt(index), "monthlyMarketRent", true, value);
        }
    };

    useEffect(() => {
        if ((props.purpose === "edit" ? (props.units.length - props.confirmedArr.length) : props.units.length)  > 10) {
            setTotalPages(Math.ceil((props.purpose === "edit" ? (props.units.length - props.confirmedArr.length) : props.units.length) / 10));
        }
        // eslint-disable-next-line
    }, []);

    const rent = <th key="nonfirm-units-wizard-heading-9">{t("rent", {ns: "suite_breakdown_table"})}</th>;
    const confirm = <th key="confirm-units-wizard-heading-10"><div><label htmlFor='confirm-units-wizard-all'>{t("confirm", {ns: "guided_wizard"})}</label></div>
        <div><Checkbox name="confirm-units-wizard-all" id="confirm-units-wizard-all"
        checked={props.confirmedArr.length === props.units.length} onChange={(e) => checkboxEventHandler(e)}/></div>
    </th>;

    const commons = (props.isBasePropType || props.purpose === "affordable-summary") ? [<th key="confirm-units-wizard-heading-1">{t("style", {ns: "suite_breakdown_table"})}</th>,
        <th key="confirm-units-wizard-heading-2">{t("type", {ns: "suite_breakdown_table"})}</th>,
        <th key="confirm-units-wizard-heading-3">{t("unitCount", {ns: "suite_breakdown_table"})}</th>,
        <th key="confirm-units-wizard-heading-4">{t("furnishedFlag", {ns: "suite_breakdown_table"})}</th>,
        <th key="confirm-units-wizard-heading-5">{t("baths", {ns: "suite_breakdown_table"})}</th>,
        <th key="confirm-units-wizard-heading-6">{t("size", {ns: "suite_breakdown_table"})}</th>,
        <th key="confirm-units-wizard-heading-7">{t("renovatedUnitCount", {ns: "suite_breakdown_table"})}</th>,
        <th key="confirm-units-wizard-heading-8">{t("vacantUnitCount", {ns: "suite_breakdown_table"})}</th>
        ] :
        [<th key="confirm-units-wizard-heading-1">{t("style", {ns: "suite_breakdown_table"})}</th>,
            <th key="confirm-units-wizard-heading-2">{t("type", {ns: "suite_breakdown_table"})}</th>,
            <th key="confirm-units-wizard-heading-3">{t("baths", {ns: "suite_breakdown_table"})}</th>,
            <th key="confirm-units-wizard-heading-4">{t("size", {ns: "suite_breakdown_table"})}</th>
        ]
    const headings = props.purpose === "affordable-summary" ? commons.slice(0, 3) : (props.purpose === "confirm" ? [...commons, confirm] : (props.purpose === "edit" ? commons : [...commons, rent]));

    const buildTableContent = (): JSX.Element => {
        const tableBody: JSX.Element[] = [];
        let indices = props.units.map((_, index) => index);
        if (props.purpose === "edit") {
            indices = indices.filter((index) => !props.confirmedArr.includes(index));
        }
        for (let index = 10 * (currentPage - 1); index < Math.min(indices.length, 10 * currentPage); index++) {
            tableBody.push (<tr key={`not-expired-lease-wizard-row-${index}`}>
                <td className={props.purpose === "edit" ? '' : 'no-input-cell'}>
                    {props.purpose === "edit" ? 
                        <Dropdown
                            options={props.styleOptions}
                            value={(props.units[indices[index]]["styleTypeCode"] as string | null) ?? ""}
                            onChange={e => updateValue(indices[index], "styleTypeCode", false, 0, e)} ariaLabel={t("style", { ns: "suite_breakdown_table" }) + " " + index}
                            name={'styleTypeCode'} 
                            testid={`wizard-style-dropdown-${index}`} />
                    :<label>{(props.styleOptions.find((style) => style["value"] === props.units[indices[index]]["styleTypeCode"])??{"text": ""})["text"] as string}</label>}
                </td>
                <td className={props.purpose === "edit" ? '' : 'no-input-cell'}>
                    {props.purpose === "edit" ?
                        <Dropdown
                            options={((props.units[indices[index]]["styleTypeCode"] as string) in props.unitTypeSubtypeMap) ? props.unitTypeSubtypeMap[props.units[indices[index]]["styleTypeCode"] as string] : [{ value: "", text: "" }]}
                            value={(props.units[indices[index]]["unitSubtypeCode"] as string | null) ?? ""}
                            onChange={e => updateValue(indices[index], "unitSubtypeCode", false, 0, e)}
                            ariaLabel={t("type", { ns: "suite_breakdown_table" }) + " " + index}
                            name={'unitSubtypeCode'} 
                            testid={`wizard-type-dropdown-${index}`}/>
                    :<p className='no-wrap'>{((props.units[indices[index]]["styleTypeCode"] === "1" ? props.multiresUnitSubtype : props.multiresRowHouseUnitSubtype).find((subtype) => subtype["unitSubtypeCode"] === props.units[indices[index]]["unitSubtypeCode"]) ?? {"unitSubtypeDescription" : ""})["unitSubtypeDescription"] as string}</p>}
                </td>
                {props.isBasePropType && 
                <td className={props.purpose === "edit" ? '' : 'no-input-cell'}>
                    {props.purpose === "edit" ?
                    <Input
                        name={`multiresUnitCount-${index}`} 
                        id={`multiresUnitCount-${index}`} 
                        type="text" 
                        value={(props.units[indices[index]].unitCount as number | null) ? (props.units[indices[index]].unitCount as number).toString() : ""} 
                        onChange={e => updateValue(indices[index], "unitCount", true, 0, e)} ariaLabel={t("unitCount", {ns: "suite_breakdown_table"}) + " " + index} 
                        positiveOnly={true}  
                        testid={`wizard-unit-count-input-${index}`}/>
                    :<p>{props.units[indices[index]]["unitCount"] as string}</p>}
                </td>}
                {props.isBasePropType && 
                <td className={'center-cell' + (props.purpose === "edit" ? '' : ' no-input-cell')}>
                    {props.purpose === "edit" ?
                    <input
                        type="checkbox" 
                        checked={Boolean(props.units[indices[index]]["furnishedFlag"])} 
                        onChange={e => updateValue(indices[index], "furnishedFlag", true, 0, e)}
                        aria-label={t("furnishedFlag", {ns: "suite_breakdown_table"}) + " " + (index + 1)} />
                    :(props.units[indices[index]]["furnishedFlag"] ? <FontAwesomeIcon icon={faCheck} /> : <></>)}
                </td>}
                <td className={props.purpose === "edit" ? '' : 'no-input-cell'}>
                    {props.purpose === "edit" ?
                        <Dropdown
                            options={props.bathsOptions}
                            value={(props.units[indices[index]].bathCount as number | null) ?? ""}
                            onChange={e => updateValue(indices[index], "bathCount", true, 0, e)} ariaLabel={t("baths", { ns: "suite_breakdown_table" }) + " " + index}
                            name={'bathCount'} 
                            testid={`wizard-bath-count-dropdown-${index}`}/>
                    :<p>{props.units[indices[index]]["bathCount"] as string}</p>}
                </td>
                <td className={props.purpose === "edit" ? '' : 'no-input-cell'}>
                    {props.purpose === "edit" ?
                    <Input 
                        name={`multiresUnit-size-${index}`} 
                        id={`multiresUnit-size-${index}`} 
                        type="text" 
                        value={(props.units[indices[index]].netLeasableArea as number | null) ? (props.units[indices[index]].netLeasableArea as number).toString() : ""}
                        onChange={e => updateValue(indices[index], "netLeasableArea", true, 0, e)} ariaLabel={t("size", {ns: "suite_breakdown_table"}) + " " + index} 
                        positiveOnly={true} 
                        testid={`wizard-unit-size-input-${index}`}/>
                    :<p>{props.units[indices[index]]["netLeasableArea"] as string}</p>}
                </td>
                {props.isBasePropType && 
                <td className={props.purpose === "edit" ? '' : 'no-input-cell'}>
                    {props.purpose === "edit" ? 
                    <Input
                        name={`multiresRenovatedUnitCount-${index}`} id={`multiresRenovatedUnitCount-${index}`} type="text"
                        value={(props.units[indices[index]].renovatedUnitCount as number | null) ? (props.units[indices[index]].renovatedUnitCount as number).toString() : ""}
                        onChange={e => updateValue(indices[index], "renovatedUnitCount", true, 0, e)} ariaLabel={t("renovatedUnitCount", {ns: "suite_breakdown_table"}) + " " + index}
                        positiveOnly 
                        testid={`wizard-renovated-input-${index}`}/>
                    :<p>{props.units[indices[index]]["renovatedUnitCount"] as string}</p>}
                </td>}
                {props.isBasePropType && 
                <td className={props.purpose === "edit" ? '' : 'no-input-cell'}>
                    {props.purpose === "edit" ? 
                    <Input
                            name={`multiresVacantUnitCount-${index}`} id={`multiresVacantUnitCount-${index}`} type="text"
                            value={(props.units[indices[index]].vacantUnitCount as number | null) ? (props.units[indices[index]].vacantUnitCount as number).toString() : ""}
                            onChange={e => updateValue(indices[index], "vacantUnitCount", true, 0, e)} ariaLabel={t("vacantUnitCount", {ns: "suite_breakdown_table"}) + " " + index}
                            positiveOnly={true} 
                            testid={`wizard-vacant-units-input-${index}`}/>
                    :<p>{props.units[indices[index]]["vacantUnitCount"] as string}</p>}
                </td>}
                {props.purpose === "rent" ? <td>
                    <CurrencyInput name={`multiresRent-${indices[index]}`} id={`multiresRent-${indices[index]}`}
                        value={(props.units[indices[index]].monthlyMarketRent as number | null)}
                        onChange={handleCurrencyChange} 
                        testid={`wizard-rent-input-${index}`}/>  
                </td> : (props.purpose === 'summary' ?  <td className='no-input-cell'><CurrencyInput value={(props.units[indices[index]].monthlyMarketRent as number | null)} disabled={true} /></td> : <></>)}
                {props.purpose === "confirm" && <td className='center-cell'>
                    <div className='center-checkbox'>
                        <Checkbox key={`multires-confirm-wizard-${index}`} name={`multires-confirm-wizard-${index}`} id={`multires-confirm-wizard-${index}`}
                            checked={props.confirmedArr.includes(index)} onChange={(e) => checkboxEventHandler(e, index)}/>
                    </div>
                </td>}
            </tr>)
        }
        return (<>{tableBody}</>);
    }

    const buildAffordableSummarTableContent = (): JSX.Element => {
        const tableBody: JSX.Element[] = [];
        const rolledDownUnits: Record<string, unknown>[] = []
        props.units.forEach((unit) => {
            if (unit.unitCount) {
                for (let i = 0; i < (unit.unitCount as number) ; i++) {
                    rolledDownUnits.push({
                        "styleTypeCode": unit.styleTypeCode,
                        "unitSubtypeCode": unit.unitSubtypeCode,
                        "unitCount": 1
                    });
                }
            }
        })
        if (rolledDownUnits.length > 0) {
            for (let index = 10 * (currentPage - 1); index < Math.min(rolledDownUnits.length, 10 * currentPage); index++) {
                tableBody.push (<tr key={`not-expired-lease-wizard-row-${index}`}>
                    <td className='no-input-cell'>
                        <label>{(props.styleOptions.find((style) => style["value"] === rolledDownUnits[index]["styleTypeCode"])??{"text": ""})["text"] as string}</label>
                    </td>
                    <td className='no-input-cell'>
                        <p className='no-wrap'>{((rolledDownUnits[index]["styleTypeCode"] === "1" ? props.multiresUnitSubtype : props.multiresRowHouseUnitSubtype).find((subtype) => subtype["unitSubtypeCode"] === rolledDownUnits[index]["unitSubtypeCode"]) ?? {"unitSubtypeDescription" : ""})["unitSubtypeDescription"] as string}</p>
                    </td>
                    <td className='no-input-cell'>
                        <p>{rolledDownUnits[index]["unitCount"] as string}</p>
                    </td>
                </tr>)
            }
        }
        return (<>{tableBody}</>);
    }

    return (<div>
        {props.purpose === 'affordable-summary' ?
        <>
            <p>{t("multires-summary-wizard.multires-affordable-description", {ns: "guided_wizard"})}</p>
            <DataTable headings={headings} className={'wizard-table multires-full-table'} totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage}>
                {buildAffordableSummarTableContent()}
            </DataTable>
        </>
        :<>
            <p>{props.purpose === 'summary' ? t("multires-summary-wizard.multires-summary-description", {ns: "guided_wizard"}) : t(`multires-confirm-units-wizard.multires-${props.purpose}-units-description`, {ns: "guided_wizard"})}</p>
            <DataTable headings={headings} className={'wizard-table multires-full-table'} totalPages={totalPages} currentPage={currentPage} changePage={setCurrentPage}>
                {buildTableContent()}
            </DataTable>
        </>}
    </div>)
}
